/**
 * Processing Monitor column-preference helpers.
 *
 * Prefs persist per tenant in localStorage under `pm_column_prefs_${tenantId}`
 * as { fixedColumns, fieldColumns, columnWidths }. columnWidths is an
 * Excel-style { [colKey]: px } map added on top of the legacy two-key shape —
 * a legacy object without it (or with garbage in it) must normalize cleanly
 * and never drop the caller's column selection.
 *
 * Widths are kept for columns the picker removes, so re-adding a column
 * restores its width. The picker's "Reset to Default" builds a fresh object
 * without columnWidths, which normalize treats as "all auto" — a full reset
 * intentionally clears widths too.
 */

export const MIN_COLUMN_WIDTH = 60;

/** Keep only sane pixel entries: finite numbers, clamped to the minimum. */
export function sanitizeColumnWidths(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw)) {
    const px = Number(value);
    if (Number.isFinite(px) && px > 0) {
      out[key] = Math.max(MIN_COLUMN_WIDTH, Math.round(px));
    }
  }
  return out;
}

/**
 * Normalize a raw (possibly legacy, possibly corrupt) prefs object into the
 * full { fixedColumns, fieldColumns, columnWidths } shape.
 */
export function normalizeColumnPrefs(raw, defaultColumns) {
  const base = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    fixedColumns: Array.isArray(base.fixedColumns) && base.fixedColumns.length > 0
      ? base.fixedColumns
      : [...defaultColumns],
    fieldColumns: Array.isArray(base.fieldColumns) ? base.fieldColumns : [],
    columnWidths: sanitizeColumnWidths(base.columnWidths),
  };
}

/** Set one column's width (clamped). Returns a new prefs object. */
export function setColumnWidth(prefs, colKey, px) {
  return {
    ...prefs,
    columnWidths: sanitizeColumnWidths({ ...prefs.columnWidths, [colKey]: px }),
  };
}

/** Merge a batch of widths in one commit (used at drag mouseup). */
export function mergeColumnWidths(prefs, widths) {
  return {
    ...prefs,
    columnWidths: sanitizeColumnWidths({ ...prefs.columnWidths, ...widths }),
  };
}

/** Reset one column to auto (double-click on its handle). */
export function clearColumnWidth(prefs, colKey) {
  const next = { ...prefs.columnWidths };
  delete next[colKey];
  return { ...prefs, columnWidths: next };
}

/** True when at least one column has an explicit width (fixed table layout). */
export function hasAnyColumnWidth(prefs) {
  return Object.keys(prefs?.columnWidths || {}).length > 0;
}
